import type { ReactNode } from "react";
import type { Promisable } from "type-fest";

import { TortusDockedPanel } from "./TortusDockedPanel.js";
import {
  TortusEmbedSurface,
  useTortusEmbed,
  type TortusAvtLaunchToken,
  type TortusEmbedCallbacks,
} from "./TortusEmbedSurface.js";

export type TortusAvtDrawerProps = TortusEmbedCallbacks & {
  open: boolean;
  onClose: () => void;
  /**
   * Async function that returns a fresh launch token. Called by the Tortus SDK
   * on init and on any subsequent token refresh. In production this hits
   * aspire-server; in Storybook it's a fake.
   */
  tokenProvider: () => Promisable<TortusAvtLaunchToken>;
  /**
   * Slot rendered in the header toolbar, to the left of the Close button. Use
   * this to surface controls that need to be clickable while the panel is open
   * (e.g. "Start consultation", "Go home").
   */
  headerActions?: ReactNode;
  /** See {@link TortusDockedPanelProps.stickyTop}. */
  stickyTop?: number;
  /** See {@link TortusDockedPanelProps.bottomInset}. */
  bottomInset?: number;
};

/**
 * The Tortus AVT embed in a {@link TortusDockedPanel}, so the form stays readable
 * and editable beside it while a consultation runs.
 *
 * Closing — or swapping this out for another stage of the flow — tears the embed
 * down, which ends any consultation in progress.
 */
export const TortusAvtDrawer = ({
  open,
  onClose,
  tokenProvider,
  headerActions,
  stickyTop = 8,
  bottomInset = 8,
  ...callbacks
}: TortusAvtDrawerProps) => {
  const { containerId, status, errorMessage } = useTortusEmbed({
    open,
    tokenProvider,
    callbacks,
  });

  if (!open) return null;

  return (
    <TortusDockedPanel
      testId="tortusAvtDrawer"
      stickyTop={stickyTop}
      bottomInset={bottomInset}
    >
      <TortusEmbedSurface
        containerId={containerId}
        status={status}
        errorMessage={errorMessage}
        onClose={onClose}
        headerActions={headerActions}
      />
    </TortusDockedPanel>
  );
};
